import { ExportFormat, ShareOutcome, type ExportResult } from '@/core/export/types'

const SHARE_TITLE = 'Insta Little Helper'

export class ShareUnavailableError extends Error {
  constructor(message = 'Sharing files is not available in this browser') {
    super(message)
    this.name = 'ShareUnavailableError'
  }
}

export function canShareFiles(files: readonly File[]): boolean {
  if (files.length === 0 || typeof navigator === 'undefined') return false
  return typeof navigator.share === 'function'
    && typeof navigator.canShare === 'function'
    && navigator.canShare({ files: [...files] }) === true
}

export function createShareCapabilityFiles(count: number, format: ExportFormat): File[] {
  if (!Number.isInteger(count) || count <= 0) throw new Error('Share capability requires a positive file count')
  if (typeof File === 'undefined') return []
  const fileType = format === ExportFormat.Jpeg
    ? { mimeType: 'image/jpeg', extension: 'jpg' }
    : format === ExportFormat.Png
      ? { mimeType: 'image/png', extension: 'png' }
      : null
  if (!fileType) throw new Error(`Share capability received unknown export format: "${String(format)}"`)
  return Array.from({ length: count }, (_, index) => new File(
    [],
    `export-${index + 1}.${fileType.extension}`,
    { type: fileType.mimeType },
  ))
}

function filesFromResults(results: readonly ExportResult[]): File[] {
  if (results.length === 0) throw new Error('Sharing requires at least one export result')
  if (typeof File === 'undefined') throw new ShareUnavailableError('Sharing files requires File API support')
  return results.map((result) => {
    if (!result.fileName.trim()) throw new Error('Shared export file name must not be empty')
    if (!result.blob.type) throw new Error(`Shared export "${result.fileName}" is missing its MIME type`)
    return new File([result.blob], result.fileName, { type: result.blob.type })
  })
}

export async function shareResults(results: readonly ExportResult[]): Promise<ShareOutcome> {
  const files = filesFromResults(results)
  if (!canShareFiles(files)) throw new ShareUnavailableError()
  try {
    await navigator.share({ files, title: SHARE_TITLE })
    return ShareOutcome.Shared
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError') {
      return ShareOutcome.Cancelled
    }
    throw error
  }
}
